/**
 * Wizard Action Bar Component
 * Single responsibility: render sticky CTA and progress; delegate onContinue/onCancel to parent.
 * Open/Closed: progress dots derived from currentStep/totalSteps.
 */

import React from 'react';
import { ArrowRight } from 'lucide-react';

const DEFAULT_CONTINUE_TEXT = 'Back to overview';

/**
 * @typedef {Object} WizardActionBarProps
 * @property {number} currentStep
 * @property {number} totalSteps
 * @property {() => void} onContinue
 * @property {() => void} [onCancel]
 * @property {string} [stepName]
 * @property {string} [continueText]
 */

/**
 * @param {WizardActionBarProps} props
 */
export const WizardActionBar = ({
  currentStep,
  totalSteps,
  onContinue,
  onCancel,
  stepName,
  continueText = DEFAULT_CONTINUE_TEXT,
}) => {
  return (
    <>
      <div className="h-[100px]" aria-hidden="true" />

      <div className="fixed bottom-0 left-0 right-0 z-40 pointer-events-none">
        <div className="absolute bottom-0 left-0 right-0 h-[120px] bg-gradient-to-t from-white via-white/95 to-transparent pointer-events-none" />

        <div className="relative pointer-events-auto">
          <div className="bg-white/80 backdrop-blur-lg border-t border-gray-200/80 shadow-[0_-4px_20px_0_rgba(0,0,0,0.08)]">
            <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-5">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="flex flex-col sm:flex-row items-center gap-3 sm:gap-4">
                  <div className="flex items-center gap-2">
                    <span className="font-['Roboto:Medium',sans-serif] font-medium text-[14px] text-gray-600">
                      Step {currentStep} of {totalSteps}
                    </span>
                    {stepName && (
                      <>
                        <span className="hidden sm:inline text-gray-400">•</span>
                        <span className="hidden sm:inline font-['Roboto:Regular',sans-serif] text-[14px] text-gray-500">
                          {stepName}
                        </span>
                      </>
                    )}
                  </div>

                  <ProgressDots currentStep={currentStep} totalSteps={totalSteps} />
                </div>

                {/* Right: Action Buttons */}
                <div className="flex items-center gap-3 w-full sm:w-auto">
                  {/* Cancel Button */}
                  {onCancel && (
                    <button
                      type="button"
                      onClick={onCancel}
                      className="flex-1 sm:flex-none border-2 border-gray-300 bg-white text-gray-700 rounded-[10px] px-6 py-2.5 font-['Roboto:Medium',sans-serif] font-medium text-[15px] hover:bg-gray-50 hover:border-gray-400 transition-all"
                    >
                      Cancel
                    </button>
                  )}

                  {/* Continue Button (always enabled: "Back to overview") */}
                  <div className="relative group flex-1 sm:flex-none">
                    <button
                      type="button"
                      onClick={onContinue}
                      className="w-full sm:w-auto flex items-center justify-center gap-2 rounded-[10px] px-8 py-2.5 font-['Roboto:Bold',sans-serif] font-bold text-[15px] min-w-[180px] transition-all bg-[#61b8e7] text-white hover:bg-[#4da3d1] hover:shadow-lg hover:shadow-[#61b8e7]/20 hover:scale-[1.02] cursor-pointer"
                      style={{ fontVariationSettings: "'wdth' 100" }}
                    >
                      <span>{continueText}</span>
                      <ArrowRight className="w-[18px] h-[18px]" strokeWidth={2.5} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

const ACTIVE_DOT_CLASS = 'w-8 h-2 bg-[#61b8e7] rounded-full';
const INACTIVE_DOT_CLASS = 'w-2 h-2 bg-gray-300 rounded-full';

/** Presentational: progress dots for wizard steps. */
function ProgressDots({ currentStep, totalSteps }) {
  const getDotClassName = (index) =>
    index < currentStep ? ACTIVE_DOT_CLASS : INACTIVE_DOT_CLASS;

  return (
    <div className="flex items-center gap-2">
      {Array.from({ length: totalSteps }).map((_, index) => (
        <div
          key={index}
          className={`transition-all duration-300 ${getDotClassName(index)}`}
          aria-hidden
        />
      ))}
    </div>
  );
}
